/**
 * Timezone utility functions for handling UTC conversion
 * Converts times to UTC when sending to backend and back to local time when displaying in UI
 */

/**
 * Converts a local datetime string (from datetime-local input) to UTC ISO string
 * @param {string} localDateTimeString - String in format "YYYY-MM-DDTHH:MM" (local time)
 * @returns {string} - UTC ISO string in format "YYYY-MM-DDTHH:MM:SS.sssZ"
 */
export const convertLocalToUTC = (localDateTimeString) => {
  if (!localDateTimeString) return null;
  
  // Create a Date object from the local datetime string
  // This will be interpreted as local time
  const localDate = new Date(localDateTimeString);
  
  // Convert to UTC ISO string
  return localDate.toISOString();
};

/**
 * Converts a UTC ISO string (Instant) to local datetime string for datetime-local input
 * @param {string} utcIsoString - UTC ISO string in format "YYYY-MM-DDTHH:MM:SS.sssZ"
 * @returns {string} - Local datetime string in format "YYYY-MM-DDTHH:MM"
 */
export const convertUTCToLocal = (utcIsoString) => {
  if (!utcIsoString) return '';
  
  // Create a Date object from the UTC ISO string (Instant format)
  const utcDate = new Date(utcIsoString);
  
  // Convert to local datetime string for datetime-local input
  // Format: YYYY-MM-DDTHH:MM
  const year = utcDate.getFullYear();
  const month = String(utcDate.getMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getDate()).padStart(2, '0');
  const hours = String(utcDate.getHours()).padStart(2, '0');
  const minutes = String(utcDate.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Converts a UTC ISO string (Instant) to a formatted display string in local time
 * @param {string} utcIsoString - UTC ISO string (Instant format)
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted local time string
 */
export const formatUTCToLocalDisplay = (utcIsoString, options = {}) => {
  if (!utcIsoString) return 'N/A';
  
  const utcDate = new Date(utcIsoString);
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };
  
  return utcDate.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Gets current local datetime string for datetime-local input
 * @returns {string} - Current local datetime in format "YYYY-MM-DDTHH:MM"
 */
export const getCurrentLocalDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Converts a local datetime string to a Date object for comparison
 * @param {string} localDateTimeString - Local datetime string
 * @returns {Date} - Date object
 */
export const localDateTimeToDate = (localDateTimeString) => {
  if (!localDateTimeString) return null;
  return new Date(localDateTimeString);
};

/**
 * Converts a UTC ISO string (Instant) to a Date object for comparison
 * @param {string} utcIsoString - UTC ISO string (Instant format)
 * @returns {Date} - Date object
 */
export const utcIsoToDate = (utcIsoString) => {
  if (!utcIsoString) return null;
  return new Date(utcIsoString);
};

/**
 * Checks if a local datetime is in the past
 * @param {string} localDateTimeString - Local datetime string
 * @returns {boolean} - True if the datetime is in the past
 */
export const isLocalDateTimeInPast = (localDateTimeString) => {
  if (!localDateTimeString) return false;
  const scheduled = new Date(localDateTimeString);
  const now = new Date();
  return scheduled < now;
};

/**
 * Checks if a UTC datetime (Instant) is in the past
 * @param {string} utcIsoString - UTC ISO string (Instant format)
 * @returns {boolean} - True if the datetime is in the past
 */
export const isUTCDateTimeInPast = (utcIsoString) => {
  if (!utcIsoString) return false;
  const scheduled = new Date(utcIsoString);
  const now = new Date();
  return scheduled < now;
};
